import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import InputBase from '@mui/material/InputBase'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import SearchIcon from '@mui/icons-material/Search'
import { useGetAuthoringComponentQuery, useGetManifestQuery, useListComponentsQuery } from '../api/authoring'
import type { ComponentSummary, Manifest, ManifestType } from '../api/authoring'
import { acceptsUpload, proseOf, typeOf } from '../model/manifest'
import { isImage, thumbnail } from '../model/derived'
import { routes, siteOf } from '../model/routes'
import { SITE_TYPE } from '../model/website'

// Finding a thing by typing its name.
//
// **This is the half of the redesign that was still missing.** Fifteen
// nav entries — one per registered type — were collapsed into six plus a
// filtered library, and the argument for doing that was that nobody
// should have to know a photo is a `Media` filed between `Link` and
// `Menu` in order to find it. That argument only holds if there is
// another way in, and until now there was not: the only way to reach a
// component was to know its type first and navigate to it. The sidebar
// has been carrying a *disabled* search box saying so.
//
// ## What it can and cannot promise, stated once
//
// **There is no search endpoint.** `GET /v1/authoring/tenants/{t}/components`
// lists and filters by `type` and nothing else; there is no `q`, no
// prefix index, no ranking. So matching happens *in this client, over
// rows it has listed*, and everything below follows from that one fact:
//
//   - It lists the tenant **unfiltered** — `type` absent is every type —
//     in pages of 100, up to `PAGES` of them, starting when the palette
//     is first opened. One unfiltered walk rather than one listing per
//     type: a per-type fan-out would be eighteen requests on the current
//     registry and nineteen the day a type is registered, it would have
//     to decide a per-type budget nobody can defend, and it would still
//     be the same rows.
//   - It searches **what it has listed, not the tenant**. Past
//     `PAGES × 100` components it is honestly blind, and it says so in
//     the footer on every keystroke rather than only when it finds
//     nothing — a search that quietly means "some of it" is worse than
//     one that is slow.
//   - It matches on **`label`** — what the type says the component calls
//     itself — and on an **id prefix**, because pasting an id out of a
//     URL or a log is a real thing an operator does. It does not match
//     body text, field values, or a page's address: none of those are on
//     a listing row, and reading 500 documents to search them is not a
//     keystroke's worth of work.
//
// The budget is deliberately generous rather than tight. A tenant here
// is one customer with one or two sites — the largest real one holds 115
// components, most of them photos — so `PAGES` of 100 covers every
// tenant this platform has several times over, and the failure it is
// sized against (an agency tenant with thousands of media) degrades into
// "it found what it had listed", not into a lie.
//
// ## What it deliberately does not do
//
// **It writes nothing.** The comp offers `New album named "helm"…` as a
// third group and it is a good idea, but a create needs a type, a
// collection and a key, and a palette that guesses any of those from a
// search string is a palette that makes components nobody asked for. The
// actions here are navigation only, which is also why it can be opened
// on any screen without asking what is unsaved.
//
// **It holds no type's name.** Which route a result opens is decided by
// the type's declared `role` — `site`, `page`, `record`, and otherwise
// the standalone component editor — and by `defines` for the meta-type,
// so a type registered tomorrow is findable tomorrow and lands somewhere
// sensible without an entry in a table here. That is the same rule the
// library, the uploader and the navigation follow.

/**
 * How many pages of the tenant's listing to walk, and how big each is.
 *
 * 100 is the API's maximum `limit`; five of them is 500 components,
 * which is several times the largest tenant this platform holds. See the
 * header for why the budget is sized to be generous rather than tight.
 */
const PAGES = 5
const PAGE_SIZE = 100

/** How many matches to draw in one group, and in total. */
const PER_GROUP = 6
const TOTAL = 14

/**
 * Whether this looks like a Mac, for the one thing that has to differ:
 * the key the reader is told to press.
 *
 * The *binding* does not differ — both modifiers open it everywhere,
 * because a keyboard is not an operating system and a Mac user on a
 * borrowed laptop presses what their fingers know. Only the hint does.
 */
export function shortcutHint(): string {
  const platform = typeof navigator === 'undefined' ? '' : navigator.platform || navigator.userAgent
  return /Mac|iPhone|iPad/.test(platform) ? '⌘K' : 'Ctrl K'
}

/**
 * The palette's open state, and the shortcut that opens it.
 *
 * A hook rather than a context because exactly two things need it — the
 * shell, which mounts the dialog, and the sidebar's search button — and
 * they are parent and child. A context here would be ceremony around one
 * boolean.
 *
 * **The shortcut refuses to fire while the reader is typing.** A global
 * key handler that swallows ⌘K inside a title field is a key handler
 * that eats an author's keystroke in the one place they are certain they
 * are talking to a text box and not to the application. The cost is
 * accepted and small: a reader who wants the palette while editing
 * presses Escape or clicks the sidebar first. The palette's *own* input
 * is not an exception worth writing, because the palette is already open
 * by then.
 */
export function useCommandPalette(): { open: boolean; show: () => void; hide: () => void } {
  const { tenant } = useParams()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    // Only inside a tenant: outside one there is nothing to search, and
    // a palette that opens onto "no tenant" teaches the reader that the
    // shortcut sometimes does nothing.
    if (!tenant) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey) || event.altKey) return
      if (isTyping(event.target)) return
      event.preventDefault()
      setOpen(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tenant])

  // Closed the moment the address changes, including a change this
  // palette did not cause: a modal left standing over the screen the
  // reader just navigated to is something they have to dismiss to see
  // what they asked for.
  const { pathname } = useLocation()
  useEffect(() => setOpen(false), [pathname])

  return {
    open: open && Boolean(tenant),
    show: useCallback(() => setOpen(true), []),
    hide: useCallback(() => setOpen(false), []),
  }
}

/**
 * Whether a key event landed somewhere the reader is entering text.
 *
 * `isContentEditable` covers the rich-text surfaces, which are not
 * inputs and are exactly where an author is most obviously typing prose.
 * A `<select>` is included because type-ahead selection is typing too.
 */
function isTyping(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null
  if (!element || typeof element.tagName !== 'string') return false
  if (element.isContentEditable) return true
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
}

/** One thing the palette can take the reader to. */
interface Result {
  key: string
  /** The words the reader typed at, and the words drawn largest. */
  name: string
  /** What is drawn under the name: an id, a type, an explanation. */
  detail?: string
  to: string
  /** The component behind it, where there is one — for its thumbnail. */
  component?: ComponentSummary
}

/** A run of results under one heading. */
interface Group {
  heading: string
  results: Result[]
  /** How many more matched than are drawn. */
  hidden: number
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { tenant = '' } = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const manifest = useGetManifestQuery({ tenant }, { skip: !tenant })

  // The sites, under the same cache key the sidebar and the site list
  // already use, so knowing which site a record belongs to costs no
  // request this app was not making.
  const sites = useListComponentsQuery({ tenant, type: SITE_TYPE }, { skip: !tenant })
  const listedSites = useMemo(() => sites.data?.components ?? [], [sites.data])
  // The site a result is opened *on*: the open one, and otherwise the
  // first the tenant has. Same fallback as the navigation, for the same
  // reason — a reader standing in the pattern library who searches for a
  // page means the page of the site they are working on, and a tenant
  // here holds one or two sites, not forty.
  const site = siteOf(pathname) ?? listedSites[0]?.id

  // **Rows, accumulated across opens.** Held here rather than in the
  // dialog's subtree so that closing the palette does not throw away the
  // walk: the second open is instant, and the pages are re-subscribed
  // (and so revalidated) while it is open.
  const [pages, setPages] = useState<ComponentSummary[][]>([])
  const [exhausted, setExhausted] = useState(false)
  useEffect(() => {
    setPages([])
    setExhausted(false)
  }, [tenant])
  const onPage = useCallback((index: number, rows: ComponentSummary[], last: boolean) => {
    setPages((current) => {
      if (current[index] === rows) return current
      const next = current.slice()
      next[index] = rows
      return next
    })
    if (last) setExhausted(true)
  }, [])

  const rows = useMemo(() => pages.flat().filter(Boolean), [pages])

  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  useEffect(() => {
    if (open) {
      setQuery('')
      setActive(0)
    }
  }, [open])

  const actions = useMemo(
    () => navigationActions(tenant, site, manifest.data, listedSites),
    [tenant, site, manifest.data, listedSites],
  )
  const groups = useMemo(
    () => assemble(query, rows, actions, tenant, site, manifest.data),
    [query, rows, actions, tenant, site, manifest.data],
  )

  // One flat list, because that is what the arrow keys move through and
  // what `aria-activedescendant` names. The headings are drawn from the
  // groups; they are never landed on.
  const flat = useMemo(() => groups.flatMap((group) => group.results), [groups])
  useEffect(() => setActive(0), [query])
  const selected = flat[Math.min(active, flat.length - 1)]

  const listRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!selected) return
    listRef.current?.querySelector(`#${optionId(selected.key)}`)?.scrollIntoView({ block: 'nearest' })
  }, [selected])

  const go = (result: Result | undefined) => {
    if (!result) return
    onClose()
    navigate(result.to)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((current) => (flat.length ? (current + 1) % flat.length : 0))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((current) => (flat.length ? (current - 1 + flat.length) % flat.length : 0))
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActive(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActive(Math.max(flat.length - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      go(selected)
    }
  }

  return (
    <>
      {/* The walk itself, mounted beside the dialog rather than inside
          it: it starts when the palette is first opened and stops when it
          closes, and it draws nothing. Each page is its own subscription
          keyed by the cursor that produced it — the same shape the
          library pages by — so nothing is held twice and paging costs
          one request per page, once. */}
      {open && tenant && <Walk tenant={tenant} index={0} remaining={PAGES} onPage={onPage} />}

      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        // Near the top, as the comp draws it: a palette centred
        // vertically moves its first result to wherever the result count
        // put it, and the first result is the one the reader is aiming
        // at with Enter.
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        // The name goes on the Paper, which is the element carrying
        // `role="dialog"`; on the Dialog itself it lands on the modal's
        // root and the dialog is announced unnamed.
        PaperProps={{ 'aria-label': 'Search', sx: { mt: '84px', borderRadius: 2, overflow: 'hidden' } }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 1.75, py: 1.25 }}>
          <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          <InputBase
            autoFocus
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search…"
            inputProps={{
              'aria-label': 'Search',
              // A combobox over a listbox, which is what this is: the
              // focus never leaves the input, so the selected option has
              // to be named rather than focused. Without this a screen
              // reader announces an empty text field while the arrow
              // keys move something it cannot see.
              role: 'combobox',
              'aria-expanded': true,
              'aria-controls': 'command-palette-results',
              'aria-autocomplete': 'list',
              'aria-activedescendant': selected ? optionId(selected.key) : undefined,
              spellCheck: false,
              autoComplete: 'off',
            }}
            sx={{ fontSize: 15 }}
          />
          <Key label="esc" />
        </Stack>
        <Divider />

        <Box
          ref={listRef}
          id="command-palette-results"
          role="listbox"
          aria-label="Results"
          sx={{ p: 0.75, maxHeight: 460, overflowY: 'auto' }}
        >
          {flat.length === 0 ? (
            <Box sx={{ px: 1.5, py: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                Nothing listed here matches <strong>{query}</strong>.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Search matches names only, never the words inside a page.
              </Typography>
            </Box>
          ) : (
            groups.map((group) => (
              <Box key={group.heading} role="group" aria-label={group.heading}>
                <Typography
                  aria-hidden
                  sx={{
                    px: 1.25,
                    pt: 1.25,
                    pb: 0.5,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                  }}
                >
                  {group.heading}
                </Typography>
                {group.results.map((result) => (
                  <Row
                    key={result.key}
                    tenant={tenant}
                    result={result}
                    manifest={manifest.data}
                    selected={result.key === selected?.key}
                    onHover={() => setActive(flat.findIndex((candidate) => candidate.key === result.key))}
                    onPick={() => go(result)}
                  />
                ))}
                {group.hidden > 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', px: 1.25, pb: 0.5 }}>
                    and {group.hidden} more — narrow it, or open the library
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>

        <Divider />
        {/* **The honest line.** It is in the footer on every keystroke
            rather than in the empty state only, because the reader who
            needs it most is the one who *did* get results and is about to
            conclude those are all of them. */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ px: 1.75, py: 1, color: 'text.disabled', flexWrap: 'wrap' }}
        >
          <Typography variant="caption">
            <Box component="kbd" sx={kbd}>
              ↑↓
            </Box>{' '}
            move
          </Typography>
          <Typography variant="caption">
            <Box component="kbd" sx={kbd}>
              ↵
            </Box>{' '}
            open
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption">
            {exhausted
              ? 'Searching by name.'
              : 'Searching by name… still loading the full list.'}
          </Typography>
        </Stack>
      </Dialog>
    </>
  )
}

const kbd = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 0.5,
  px: 0.5,
  fontFamily: 'inherit',
  fontSize: 10,
}

function Key({ label }: { label: string }) {
  return (
    <Box component="kbd" sx={{ ...kbd, color: 'text.disabled', whiteSpace: 'nowrap' }}>
      {label}
    </Box>
  )
}

function optionId(key: string): string {
  return `command-palette-${key.replace(/[^A-Za-z0-9_-]/g, '-')}`
}

/**
 * One page of the walk, and the next one after it.
 *
 * Recursive rather than a loop, because a cursor is only knowable once
 * the page before it has answered — so the pages *are* a chain, and a
 * chain of components mounting one hook each is the honest expression of
 * it. It also means the walk stops by itself: a page that comes back
 * without a cursor renders no successor, and `last` says the listing is
 * exhausted rather than merely short (a short page does not).
 *
 * It renders nothing at all. Its entire output is the `onPage` call.
 */
function Walk({
  tenant,
  cursor,
  index,
  remaining,
  onPage,
}: {
  tenant: string
  cursor?: string
  index: number
  remaining: number
  onPage: (index: number, rows: ComponentSummary[], last: boolean) => void
}) {
  // No `type`, which the surface reads as every type — one walk for the
  // whole tenant rather than one per registered type.
  const listing = useListComponentsQuery({ tenant, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) })
  const rows = listing.data?.components
  const next = listing.data?.cursor
  const last = Boolean(rows) && (!next || remaining <= 1)

  useEffect(() => {
    if (rows) onPage(index, rows, last)
  }, [rows, index, last, onPage])

  if (!next || remaining <= 1) return null
  return <Walk tenant={tenant} cursor={next} index={index + 1} remaining={remaining - 1} onPage={onPage} />
}

/**
 * One result.
 *
 * Not a `ListItemButton`: an option in a listbox is not a button, and
 * the row must not take focus — the focus stays in the input and the
 * selection is carried by `aria-activedescendant`. Hovering moves the
 * selection rather than drawing a second highlight, so the mouse and the
 * arrow keys never disagree about which row Enter would open.
 */
function Row({
  tenant,
  result,
  manifest,
  selected,
  onHover,
  onPick,
}: {
  tenant: string
  result: Result
  manifest: Manifest | undefined
  selected: boolean
  onHover: () => void
  onPick: () => void
}) {
  return (
    <Stack
      id={optionId(result.key)}
      role="option"
      aria-selected={selected}
      direction="row"
      spacing={1.25}
      alignItems="center"
      onMouseMove={onHover}
      onClick={onPick}
      sx={{
        px: 1.25,
        py: 0.875,
        borderRadius: 1,
        cursor: 'pointer',
        bgcolor: selected ? 'action.selected' : 'transparent',
      }}
    >
      <Thumb tenant={tenant} component={result.component} manifest={manifest} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 600, color: selected ? 'primary.main' : 'text.primary' }}
        >
          {result.name}
        </Typography>
        {result.detail && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'block', fontFamily: result.component ? 'monospace' : undefined }}
          >
            {result.detail}
          </Typography>
        )}
      </Box>
      {selected && <Key label="↵" />}
    </Stack>
  )
}

/**
 * The picture beside a result, where the thing has one.
 *
 * **Two reads, and only for rows on screen.** A listing row carries no
 * thumbnail — which renditions exist is a fact the *document* carries,
 * because the platform skips a width above the original and makes none
 * for something that is not an image — so the grid in the library pays a
 * point read per tile and so does this. What is drawn is bounded to
 * `TOTAL` rows, the reads are cached by `(tenant, id)`, and opening the
 * result afterwards therefore costs nothing.
 *
 * The second read is the one hop that makes this worth doing at all: an
 * album is not a picture, it *points* at one, so a component with no
 * bytes of its own is asked for the first reference it holds to a type
 * that `acceptsUpload` — the cover, without this client knowing that a
 * cover is called a cover or that a photo is called a Media. One hop and
 * no further: a chain of pointers is a graph walk, not a thumbnail.
 */
function Thumb({
  tenant,
  component,
  manifest,
}: {
  tenant: string
  component: ComponentSummary | undefined
  manifest: Manifest | undefined
}) {
  const own = useGetAuthoringComponentQuery({ tenant, id: component?.id ?? '' }, { skip: !component })
  const cover = (own.data?.references ?? []).find((reference) => acceptsUpload(manifest, reference.type))
  const held = useGetAuthoringComponentQuery({ tenant, id: cover?.id ?? '' }, { skip: !cover })

  const source = isImage(own.data) ? own.data : isImage(held.data) ? held.data : undefined
  const url = thumbnail(source, 320)

  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'action.hover',
        color: 'text.disabled',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      {url ? (
        <Box component="img" src={url} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : component ? null : (
        <ArrowForwardIcon sx={{ fontSize: 16 }} />
      )}
    </Box>
  )
}

/**
 * Where a component of this type is opened.
 *
 * **Decided by the type's declared `role`, never by its name.** A site
 * has a site's screen, a page has a page's, a record that belongs to a
 * Collection has the owner's record screen, a type definition has the
 * type designer, and everything else has the standalone component
 * editor, which every type has. So a type registered tomorrow lands
 * somewhere sensible tomorrow with nothing added here.
 *
 * The site-scoped ones need a site and fall back to the standalone
 * editor without one, which is right rather than merely safe: a page
 * genuinely belongs to a site, and addressing it under a site the tenant
 * does not have would be inventing a route.
 */
function destination(
  tenant: string,
  site: string | undefined,
  declared: ManifestType | undefined,
  summary: ComponentSummary,
): string {
  if (declared?.defines && summary.label) return routes.type(tenant, summary.label)
  if (declared?.role === 'site') return routes.site(tenant, summary.id)
  if (site && declared?.role === 'page') return routes.page(tenant, site, summary.id)
  if (site && declared?.role === 'record' && declared.collection) {
    return routes.record(tenant, site, declared.name, summary.id)
  }
  return routes.component(tenant, summary.id)
}

/**
 * The order the groups are drawn in.
 *
 * Not the registry's order and not alphabetical: what a site's owner
 * came looking for, then what an agency did. A record is the thing an
 * owner names and edits every week; a page is the thing they publish; a
 * block is a piece of one of those and is usually reached *through* it.
 * A type with no role sorts with the blocks, because a type that
 * declares nothing about what it is has not earned the top of the list.
 */
const ROLE_ORDER: Array<ManifestType['role']> = ['record', 'page', 'site', 'layout', 'block', undefined]

/**
 * Turn what has been listed into the groups on screen.
 *
 * Actions first when nothing is typed — an empty palette that lists 500
 * components is a list, not an answer, and the useful thing to offer
 * somebody who opened it and paused is where they can go. Once something
 * *is* typed, what the tenant holds comes first and the actions fall to
 * the bottom: the reader typing "helm" wants the album, not the Releases
 * screen, even though both match nothing and something respectively.
 */
function assemble(
  query: string,
  rows: ComponentSummary[],
  actions: Result[],
  tenant: string,
  site: string | undefined,
  manifest: Manifest | undefined,
): Group[] {
  const needle = query.trim().toLowerCase()

  if (!needle) {
    return [{ heading: 'Go to', results: actions.slice(0, TOTAL), hidden: Math.max(actions.length - TOTAL, 0) }]
  }

  // Matched, scored and bucketed by type in one pass. The score is the
  // only ranking there is and it is deliberately crude: a name that
  // starts with what was typed, then one whose *word* starts with it,
  // then one that merely contains it. Anything cleverer would be a
  // relevance model built on a field the server does not index.
  const byType = new Map<string, Array<{ summary: ComponentSummary; score: number }>>()
  for (const summary of rows) {
    const score = match(needle, summary)
    if (score === 0) continue
    const bucket = byType.get(summary.type) ?? []
    bucket.push({ summary, score })
    byType.set(summary.type, bucket)
  }

  const groups: Group[] = []
  const ordered = [...byType.entries()].sort((left, right) => {
    const leftRole = ROLE_ORDER.indexOf(typeOf(manifest, left[0])?.role)
    const rightRole = ROLE_ORDER.indexOf(typeOf(manifest, right[0])?.role)
    if (leftRole !== rightRole) return leftRole - rightRole
    return left[0].localeCompare(right[0])
  })

  let drawn = 0
  for (const [type, matched] of ordered) {
    if (drawn >= TOTAL) break
    const declared = typeOf(manifest, type)
    const prose = proseOf(declared, type)
    matched.sort((left, right) => right.score - left.score || label(left.summary).localeCompare(label(right.summary)))
    const room = Math.min(PER_GROUP, TOTAL - drawn)
    const shown = matched.slice(0, room)
    drawn += shown.length
    groups.push({
      // The type's own words — "Albums", "Photo galleries" — never the
      // wire name. A reader who has to know that a photo is a `Media` to
      // read the search results is back where this started.
      heading: prose.plural,
      hidden: matched.length - shown.length,
      results: shown.map(({ summary }) => ({
        key: summary.id,
        name: label(summary),
        // **Always a second line, and it is the id.** Two components
        // may honestly share a name — an album and the gallery it owns
        // are both "Helmets" — and two identical rows in two groups is
        // the palette asking the reader to guess. The id is the only
        // thing that is certainly different, it is what the address bar
        // will say, and it is what an operator pastes into a log.
        detail: summary.label ? shortId(summary.id) : `Unnamed ${prose.title.toLowerCase()} · ${shortId(summary.id)}`,
        to: destination(tenant, site, declared, summary),
        component: summary,
      })),
    })
  }

  const matchedActions = actions.filter((action) => action.name.toLowerCase().includes(needle))
  if (matchedActions.length) {
    groups.push({ heading: 'Go to', results: matchedActions.slice(0, PER_GROUP), hidden: 0 })
  }
  return groups
}

function label(summary: ComponentSummary): string {
  return summary.label ?? summary.id
}

/**
 * Enough of an id to tell two rows apart, which is all a result line
 * needs. The whole thing is 36 characters and would wrap the row; the
 * screen it opens says the id in full.
 */
function shortId(id: string): string {
  return id.slice(0, 8)
}

/**
 * How well a row answers what was typed. Zero is no answer at all.
 *
 * The id is matched by prefix only, and scores lowest. Pasting an id out
 * of a URL or an error is something an operator genuinely does; matching
 * an id by substring is how a hex string starting `ef07` matches four
 * unrelated components.
 */
function match(needle: string, summary: ComponentSummary): number {
  const name = (summary.label ?? '').toLowerCase()
  if (name.startsWith(needle)) return 3
  if (name.split(/[\s/_-]+/).some((word) => word.startsWith(needle))) return 2
  if (name.includes(needle)) return 1
  if (summary.id.toLowerCase().startsWith(needle)) return 1
  return 0
}

/**
 * Where the reader can go, as things they can type at.
 *
 * Built from `routes` and from what the manifest says this tenant has —
 * no Content types entry for a build that registers no meta-type, no
 * Patterns entry for one that declares none — so the palette never
 * offers a screen that would render "this build declares none of these".
 *
 * The per-site entries appear only where the tenant has more than one
 * site, because with exactly one "Overview" already *is* that site and
 * two rows going to the same address is the palette disagreeing with
 * itself.
 */
function navigationActions(
  tenant: string,
  site: string | undefined,
  manifest: Manifest | undefined,
  sites: ComponentSummary[],
): Result[] {
  if (!tenant) return []
  const results: Result[] = []
  const add = (name: string, detail: string, to: string) => results.push({ key: `go:${to}`, name, detail, to })

  if (site) {
    add('Overview', 'What is waiting to go live, and what is quietly wrong', routes.site(tenant, site))
    add('Pages', 'Every page of the site, in order', routes.pages(tenant, site))
    add('Releases', 'What is published, and what is pending', routes.siteReleases(tenant, site))
    add('Enquiries', 'What visitors have sent through the site\'s forms', routes.enquiries(tenant, site))
    add('Settings', 'The site\'s name, theme, search details and web addresses', routes.siteSettings(tenant, site))
    // The guide, under this site for the reason the sidebar opens it
    // there: the menu beside it keeps the groups it describes.
    add('Help', 'How to look after your website with this admin', routes.help(tenant, site))
  }
  if ((manifest?.types ?? []).some((declared) => declared.defines)) {
    add('Content types', 'What this tenant has declared for itself', routes.types(tenant))
  }
  if ((manifest?.types ?? []).some((declared) => declared.declaresPatterns)) {
    add('Patterns', 'The patterns this tenant has declared', routes.patterns(tenant))
  }
  add('Components', 'Everything the tenant holds, of every type', routes.components(tenant))
  add('Integrations', 'What this tenant has asked to be told about', routes.integrations(tenant))
  // The reader's own, not the tenant's — but reachable from any screen,
  // and the palette is where a reader who cannot see the sidebar's foot
  // goes looking for "password" or "two-step".
  add('Account', 'Your name, password, two-step verification and sessions', routes.account())

  if (sites.length > 1) {
    for (const candidate of sites) {
      add(candidate.label ?? candidate.id, 'Open this site', routes.site(tenant, candidate.id))
    }
  }
  return results
}
